import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import TipTapEditor from "../components/editor/TipTapEditor";
import AdminLayout from "../components/AdminLayout";
import { categories } from "../data/blogData";
import {
  createPost,
  updatePost,
  getPostById,
} from "../services/blogService";

const POST_CATEGORIES = categories.filter((c) => c !== "All Posts");

export default function AdminEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: POST_CATEGORIES[0],
    tags: [],
    image: "",
    published: false,
  });
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [previewMode, setPreviewMode] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      getPostById(id)
        .then((post) => {
          if (post) {
            setFormData({
              title: post.title || "",
              excerpt: post.excerpt || "",
              content: post.content || "",
              category: post.category || POST_CATEGORIES[0],
              tags: post.tags || [],
              image: post.image || "",
              published: post.published || false,
            });
          } else {
            setError("Post not found");
          }
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing]);

  function handleChange(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  function handleAddTag(e) {
    e.preventDefault();
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      handleChange("tags", [...formData.tags, tag]);
    }
    setTagInput("");
  }

  function handleRemoveTag(tag) {
    handleChange(
      "tags",
      formData.tags.filter((t) => t !== tag)
    );
  }

  async function handleImageUpload(file) {
    if (!file || !file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be under 5MB");
      return;
    }

    setUploading(true);
    setError("");
    try {
      const storageRef = ref(storage, `blog-covers/${Date.now()}-${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      handleChange("image", url);
    } catch (err) {
      setError("Failed to upload image: " + (err.message || "Unknown error"));
    } finally {
      setUploading(false);
    }
  }

  function handlePaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        handleImageUpload(file);
        return;
      }
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleImageUpload(file);
  }

  function handleDragOver(e) {
    e.preventDefault();
  }

  async function handleSave(publish) {
    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const postData = {
        ...formData,
        published: publish,
        author: "Carium Tech Team",
      };

      if (isEditing) {
        await updatePost(id, postData);
      } else {
        await createPost(postData);
      }
      navigate("/admin");
    } catch (err) {
      setError(err.message || "Failed to save post");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <div className="w-10 h-10 border-2 border-cyber-blue border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading post...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">
              {isEditing ? "Edit Post" : "New Post"}
            </h1>
            <p className="text-gray-400 mt-1">
              {isEditing
                ? "Update your blog post"
                : "Create a new blog post"}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className="px-4 py-2 rounded-xl text-sm font-medium glass text-gray-300 hover:text-white hover:bg-white/10 transition-all"
            >
              {previewMode ? "Edit" : "Preview"}
            </button>
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="px-4 py-2 rounded-xl text-sm font-medium glass text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10 transition-all disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Draft"}
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="px-5 py-2 rounded-xl text-sm font-semibold text-black bg-gradient-to-r from-cyber-blue to-tech-green hover:shadow-[0_0_20px_rgba(0,163,255,0.3)] transition-all disabled:opacity-50"
            >
              {saving ? "Publishing..." : "Publish"}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {previewMode ? (
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold mb-4">{formData.title || "Untitled"}</h2>
                {formData.excerpt && (
                  <p className="text-gray-400 italic mb-6">{formData.excerpt}</p>
                )}
                <div
                  className="prose prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: formData.content }}
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    placeholder="Your post title..."
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-lg placeholder-gray-600 focus:outline-none focus:border-cyber-blue focus:shadow-[0_0_12px_rgba(0,163,255,0.15)] transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Excerpt
                  </label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => handleChange("excerpt", e.target.value)}
                    placeholder="Brief summary for the blog card..."
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-cyber-blue focus:shadow-[0_0_12px_rgba(0,163,255,0.15)] transition-all resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Content
                  </label>
                  <TipTapEditor
                    content={formData.content}
                    onChange={(html) => handleChange("content", html)}
                    placeholder="Write your story..."
                  />
                </div>
              </>
            )}
          </div>

          <div className="space-y-6">
            <div className="glass rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
                Cover Image
              </h3>
              <div
                ref={dropRef}
                onPaste={handlePaste}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                tabIndex={0}
                className="w-full border-2 border-dashed border-white/10 rounded-xl p-6 text-center cursor-pointer hover:border-cyber-blue/50 hover:bg-white/[0.02] transition-all mb-3"
                onClick={() => dropRef.current?.focus()}
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-8 h-8 border-2 border-cyber-blue border-t-transparent rounded-full animate-spin" />
                    <p className="text-gray-400 text-sm">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-8 h-8 fill-gray-500">
                      <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
                    </svg>
                    <p className="text-gray-400 text-sm">
                      Paste, drop, or click to upload
                    </p>
                    <p className="text-gray-600 text-xs">PNG, JPG up to 5MB</p>
                  </div>
                )}
              </div>
              <input
                type="url"
                value={formData.image}
                onChange={(e) => handleChange("image", e.target.value)}
                placeholder="Or paste an image URL..."
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-cyber-blue transition-all mb-3"
              />
              {formData.image && (
                <img
                  src={formData.image}
                  alt="Cover preview"
                  className="w-full h-40 object-cover rounded-xl"
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
            </div>

            <div className="glass rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
                Category
              </h3>
              <select
                value={formData.category}
                onChange={(e) => handleChange("category", e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-cyber-blue transition-all appearance-none cursor-pointer"
              >
                {POST_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat} className="bg-[#0a0a0a]">
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="glass rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
                Tags
              </h3>
              <form onSubmit={handleAddTag} className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag..."
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-cyber-blue transition-all"
                />
                <button
                  type="submit"
                  className="px-3 py-2 rounded-xl text-sm font-medium glass text-gray-300 hover:text-white hover:bg-white/10 transition-all"
                >
                  +
                </button>
              </form>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-cyber-blue/10 text-cyber-blue"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-white transition-colors"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AdminLayout>
  );
}
